//@flow
import * as React from 'react';
import theme from './FpTourEventComponent.scss';
import classnames from 'classnames';
import type { Map } from 'immutable';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import throttle from 'lodash/throttle';
import { translate } from 'react-i18next';

import camelCase from 'lodash/camelCase';
import Icon from 'common/components/core/icon';
import { TooltipIconWithHighlight } from 'common/components/core/markers/IconWithHighlight';
import { TooltipDiv } from 'common/components/core/wrappers/Div';
import { dashboardColors } from 'common/enums/ColorEnum';
import TimeUtils from 'common/utils/TimeUtils';
import ColorUtils from 'common/utils/ColorUtil';
import { Box, Position, Icon as NeoIcon, Tooltip } from '@locus-taxy/neo-ui';
import { type EditVisitType } from './planEdits/PlanEditContext';

type Props = {
    event: Map<string, Object>,
    style: Object,
    className: string,
    isRowSelected: boolean,
    enableVisitTypeArrows: boolean,
    onContextMenu: (
        x: number,
        y: number,
        beatId: string,
        visitId: string
    ) => void,
    onMouseOver: (number, string, string, string) => void,
    onMouseOut: (number, string, string, string) => void,
    onSelect: (number, string, string, string) => void,
    maxEditLimitReached: boolean,
    edits: EditVisitType
};

export const FpEventComponentTypes = Object.freeze({
    TASK_VISIT: 'TASK_VISIT',
    BREAK: 'BREAK',
    RELOAD: 'RELOAD',
    LUNCH: 'LUNCH',
    SHIFT_START_VISIT: 'SHIFT_START',
    SHIFT_END_VISIT: 'SHIFT_END',
    TOUR_BACKGROUND: 'TOUR_BACKGROUND',
    OPERATION_SHIFT: 'OPERATION_SHIFT'
});

type State = {
    hovered: boolean
};

class FpTourEventComponent extends React.Component<Props, State> {
    constructor(props) {
        super(props);
        this.emitOnMouseOver = throttle(this.emitOnMouseOver, 300, {
            leading: false
        });
        this.state = {
            hovered: false
        };
    }

    componentWillUnmount() {
        this.onMouseOut();
    }

    emitOnMouseOver = () => {
        const { hovered } = this.state;
        if (!hovered) {
            return;
        }
        const { event, onMouseOver } = this.props;
        onMouseOver &&
            onMouseOver(
                event.get('beatId'),
                event.get('outletVisitId') || event.get('visitId')
            );
    };

    onMouseOver = () => {
        this.setState({ hovered: true }, this.emitOnMouseOver);
    };

    onMouseOut = () => {
        const { hovered } = this.state;
        if (!hovered) {
            return;
        }
        const { onMouseOut } = this.props;
        onMouseOut && onMouseOut();
        this.setState({ hovered: false });
    };

    onSelect = () => {
        const { event, onVisitSelect } = this.props;
        if (event.get('outletVisitId')) {
            onVisitSelect &&
                onVisitSelect(event.get('beatId'), event.get('outletVisitId'));
        }
        onVisitSelect &&
            onVisitSelect(event.get('beatId'), event.get('visitId'));
    };

    onRightClick = (e: Event) => {
        e.preventDefault();
        const { event, onContextMenu, status } = this.props;
        const beatId = event.get('beatId');
        const visitId = event.get('outletVisitId');
        if (status !== 'FROZEN' && visitId) {
            onContextMenu(e.clientX, e.clientY, beatId, visitId);
        }
    };

    renderTaskVisitEvent(label) {
        const {
            event,
            style,
            className,
            isRowSelected,
            enableVisitTypeArrows,
            maxEditLimitReached,
            t,
            edits
        } = this.props;
        const eventType = camelCase(event.get('type'));
        let extendedStyle = style;
        const labelLength = label.toString().length;
        const hideLabel =
            (extendedStyle.width <= 12 && labelLength >= 2) ||
            (extendedStyle.width <= 8 && labelLength >= 1);
        const visitEdit = edits?.find(
            ({ visitId }) => event.get('outletVisitId') === visitId
        );
        const tooltipIconWithHighlightClasses = classnames(
            className,
            theme.event,
            theme[`${eventType}Event`],
            theme.taskVisitEvent,
            {
                [theme.hovered]: event.get('highlight')
            }
        );
        const hovered = event.get('hovered');
        const selected = event.get('selected');
        if (selected) {
            extendedStyle = {
                ...style,
                backgroundColor: dashboardColors.black.code,
                color: dashboardColors.white.code,
                zIndex: '12',
                borderColor: dashboardColors.white.code
            };
        } else if (hovered) {
            extendedStyle = {
                ...style,
                backgroundColor: dashboardColors.black.code,
                color: dashboardColors.white.code
            };
        }

        const taskTypeIndicatorClasses = classnames(theme.taskTypeIndicator, {
            [theme.isRowSelected]: isRowSelected && enableVisitTypeArrows,
            [theme.arrowsToLines]: hideLabel
        });
        const taskTypeIndicatorStyles = {
            color: event.getIn(['color', 'code'])
        };

        const divExtendedStyle = pick(extendedStyle, ['width', 'left']);
        const innerStyles = omit(extendedStyle, ['width', 'left']);
        const breaches = event.get('breaches');
        return (
            <Tooltip
                tooltip={
                    maxEditLimitReached
                        ? t('entityManagement:fpPlanView.editLimitReached')
                        : undefined
                }
            >
                <div
                    style={divExtendedStyle}
                    onContextMenu={
                        maxEditLimitReached ? undefined : this.onRightClick
                    }
                    className={theme.taskEventWrapper}
                    onClick={this.onSelect}
                >
                    <TooltipIconWithHighlight
                        markerIcon={null}
                        invert
                        id={event.get('id')}
                        style={innerStyles}
                        theme={theme}
                        className={tooltipIconWithHighlightClasses}
                        onMouseOver={this.onMouseOver}
                        onMouseOut={this.onMouseOut}
                    >
                        {hideLabel ? null : label}
                    </TooltipIconWithHighlight>
                    {breaches && breaches.size ? (
                        <Position
                            position="absolute"
                            top="-4px"
                            right="-4px"
                            sx={{
                                borderRadius: '4px',
                                zIndex: 12
                            }}
                        >
                            <NeoIcon icon="circle" color="red.500" />
                        </Position>
                    ) : null}
                    {visitEdit ? (
                        <Position
                            position="absolute"
                            bottom="-7px"
                            right="-2px"
                            sx={{
                                borderRadius: '4px',
                                zIndex: 12
                            }}
                        >
                            <Tooltip
                                tooltip={t(
                                    'entityManagement:fpPlanView.visitMovedFrom',
                                    { beatId: visitEdit.originalBeatId }
                                )}
                                shouldWrapChildren
                            >
                                <NeoIcon icon="circle" color="violet.400" />
                            </Tooltip>
                        </Position>
                    ) : null}
                    <div
                        className={taskTypeIndicatorClasses}
                        style={taskTypeIndicatorStyles}
                    />
                </div>
            </Tooltip>
        );
    }

    renderOperationShiftEvent(label) {
        const { event, style, className } = this.props;
        let extendedStyle = {
            ...style,
            background: dashboardColors.white.code
        };
        const hovered = event.get('highlight');
        const selected = event.get('selected');
        if (selected) {
            extendedStyle = {
                ...style,
                backgroundColor: ColorUtils.shadeColor(
                    dashboardColors.paleGreyThree.code,
                    -0.15
                ),
                color: dashboardColors.white.code,
                zIndex: '12',
                borderColor: dashboardColors.white.code
            };
        } else if (hovered) {
            extendedStyle = {
                ...style,
                backgroundColor: ColorUtils.shadeColor(
                    dashboardColors.paleGreyThree.code,
                    -0.15
                ),
                color: dashboardColors.white.code
            };
        }
        // TODO: Deprecated product
        const operatingHours = TimeUtils.formatTimeRange(
            event.get('startTime'),
            event.get('endTime'),
            event.get('startTime')
        );
        return (
            <TooltipDiv
                tooltip={operatingHours}
                tooltipPosition="right"
                activateTooltipOn="mouse"
                invert
                theme={theme}
                tooltipDelay={100}
                style={extendedStyle}
                tooltipClassName={theme.tooltipSingleLine}
                className={classnames(
                    className,
                    theme.event,
                    theme.operationShiftEvent,
                    {
                        [theme.hovered]: event.get('highlight')
                    }
                )}
                onContextMenu={this.onRightClick}
                onMouseOver={this.onMouseOver}
                onMouseOut={this.onMouseOut}
            >
                {label}
            </TooltipDiv>
        );
    }

    _renderStartAndEndEvent(label) {
        const { style } = this.props;
        const extendedStyle = { ...style, zIndex: 12, alignSelf: 'center' };
        const { width, left, ...innerStyles } = extendedStyle;
        const divExtendedStyle = { width, left };
        // replace below div with neo cpomponent
        return (
            <div className={theme.taskEventWrapper} style={divExtendedStyle}>
                {label === 'flag' ? (
                    <NeoIcon
                        icon="dispatcher_visit_start"
                        color="grey.500"
                        size="small"
                        sx={innerStyles}
                    />
                ) : (
                    <NeoIcon
                        icon="dispatcher_visit_end"
                        color="grey.500"
                        size="small"
                        sx={innerStyles}
                    />
                )}
            </div>
        );
    }

    renderHomebaseAndBreakEvent(label) {
        const { event, style, className } = this.props;
        let extendedStyle = style;
        const hovered = event.get('highlight');
        const selected = event.get('selected');
        if (selected) {
            extendedStyle = {
                ...style,
                backgroundColor: '#F3F4F6',
                color: dashboardColors.white.code,
                zIndex: '12',
                borderColor: dashboardColors.white.code
            };
        } else if (hovered) {
            extendedStyle = {
                ...style,
                backgroundColor: '#F3F4F6',
                color: dashboardColors.white.code
            };
        }
        const divExtendedStyle = pick(extendedStyle, ['width', 'left']);
        const innerStyles = omit(extendedStyle, ['width', 'left']);
        return (
            <div
                className={theme.taskEventWrapper}
                style={divExtendedStyle}
                onContextMenu={this.onRightClick}
                onClick={this.onSelect}
            >
                <TooltipIconWithHighlight
                    theme={theme}
                    invert
                    small
                    style={innerStyles}
                    className={classnames(
                        className,
                        theme.event,
                        theme.homebaseEvent,
                        {
                            [theme.hovered]: event.get('highlight')
                        }
                    )}
                    onMouseOver={this.onMouseOver}
                    onMouseOut={this.onMouseOut}
                >
                    {label}
                </TooltipIconWithHighlight>
            </div>
        );
    }
    renderTourBackground() {
        const { style, event } = this.props;
        const color = event.get('color');

        return (
            <Box
                sx={style}
                className={theme.eventsBackgroundWrap}
                backgroundColor={color.code}
            />
        );
    }

    render() {
        const { event } = this.props;
        let label = event.get('label');
        if (isNaN(label)) {
            label = <Icon icon={label} />;
        }
        switch (event.get('id')) {
            case FpEventComponentTypes.OPERATION_SHIFT:
                return null;
            case FpEventComponentTypes.SHIFT_START_VISIT:
            case FpEventComponentTypes.SHIFT_END_VISIT:
                return this._renderStartAndEndEvent(label);
            case FpEventComponentTypes.TOUR_BACKGROUND:
                return this.renderTourBackground();
            case FpEventComponentTypes.RELOAD:
            case FpEventComponentTypes.BREAK:
            case FpEventComponentTypes.LUNCH:
                return this.renderHomebaseAndBreakEvent(label);
            case FpEventComponentTypes.TASK_VISIT:
            default:
                return this.renderTaskVisitEvent(label);
        }
    }
}

export default translate('translations')(FpTourEventComponent);
